import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { InvitationPreview, LETTER_ASPECT } from '../InvitationPreview';
import { assetUrl } from '../design/DesignLayers';
import { SampleInvitation } from './SampleInvitation';

/**
 * The fanned invitations in the home page hero: real templates from the catalog,
 * one card at a time changing to the next design every few seconds.
 * Falls back to the decorative sample cards when the catalog can't be loaded.
 */

// Slot positions, left → right. The fallback tone/kicker/names are the old decorative cards.
const SLOTS = [
  { cls: 'hidden md:block left-[14%] top-[110px] w-[210px] -rotate-[14deg] z-[1]', wide: true, tone: 'olive', kicker: 'תנאים', names: ['שם החתן', 'שם הכלה'] },
  { cls: 'hidden md:block right-[14%] top-[110px] w-[210px] rotate-[14deg] z-[1]', wide: true, tone: 'terracotta', kicker: 'קידוש', names: ['שם הבת'] },
  { cls: 'left-[4%] md:left-[27%] top-[70px] w-[150px] sm:w-[230px] -rotate-[7deg] z-[2]', tone: 'midnight', kicker: 'בר מצוה', names: ['שם הבר מצוה'] },
  { cls: 'right-[4%] md:right-[27%] top-[70px] w-[150px] sm:w-[230px] rotate-[7deg] z-[2]', tone: 'onyx', kicker: 'שבע ברכות', names: ['שם החתן', 'שם הכלה'] },
  { cls: 'left-1/2 -translate-x-1/2 top-[30px] w-[190px] sm:w-[280px] z-[3]', tone: 'ivory', kicker: 'שמחת נישואין', names: ['שם החתן', 'שם הכלה'] },
];

// Which card gets a design first and changes next: the centre one, then outwards.
const CHANGE_ORDER = [4, 2, 3, 0, 1];
const POOL_SIZE = 12;
const CHANGE_EVERY_MS = 3200;
const FIRST_LOAD_TIMEOUT_MS = 3500;
// A picture still loading after this stops holding up the others: another one starts alongside it,
// and the slow one keeps loading and is used once it arrives. Only a real load error rules a picture out.
const SLOW_MS = 4000;

/** Up to POOL_SIZE templates, taking turns between categories so the fan shows a mix of simchas. */
export const pickShowcase = (templates) => {
  const byCategory = new Map();
  const seen = new Set();
  for (const t of templates || []) {
    // side-view mockups are photos of a card at an angle; they look odd fanned out. Templates without a
    // thumbnail are left out too: their full-size background is far too heavy for a decorative card.
    if (!t || !t.thumbnail_image || t.perspective) continue;
    // some thumbnails (e.g. partner templates) are paths on the API server; load and show the full address
    const thumbnail = assetUrl(t.thumbnail_image);
    // several templates share one background; show each picture once
    if (seen.has(thumbnail)) continue;
    seen.add(thumbnail);
    const key = t.category_id || '';
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key).push(thumbnail === t.thumbnail_image ? t : { ...t, thumbnail_image: thumbnail });
  }
  const lists = [...byCategory.values()];
  const picked = [];
  for (let round = 0; picked.length < POOL_SIZE && lists.some((l) => l.length > round); round += 1) {
    for (const list of lists) {
      if (list[round] && picked.length < POOL_SIZE) picked.push(list[round]);
    }
  }
  return picked;
};

const useMediaQuery = (query) => {
  const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
};

// The design is drawn at its own letter shape (so the text sits where it does on the artwork) and the
// 5:7 card shows its middle, the way a cover-cropped picture would.
const TemplateCard = ({ template }) => (
  <div className="absolute inset-0 flex justify-center">
    <div className="h-full shrink-0" style={{ aspectRatio: LETTER_ASPECT }}>
      <InvitationPreview
        backgroundImage={template.thumbnail_image}
        fields={template.text_fields}
        getText={(field) => field.default_text || field.text || ''}
        textShadow="0 1px 3px rgba(0,0,0,0.3), 0 1px 1px rgba(255,255,255,0.8)"
        alt=""
        className="h-full w-full"
      />
    </div>
  </div>
);

/**
 * The loading and the cycling share one state and one step, `pump`, which runs after anything
 * changes (a picture settles, the catalog arrives, the screen is resized, a card changes, the first
 * cards are revealed). A card only ever gets a design whose picture has already loaded, so nothing
 * waits on a picture and two cards can never be working on the same one.
 */
const createEngine = (setShown, onScreen, initial) => {
  const st = {
    mounted: false,
    revealed: false,
    timer: null,
    // what each card has (a template, or null for the sample card); `shown` follows it once revealed
    slots: SLOTS.map(() => null),
    // picture src -> { status: 'loading' | 'ok' | 'error', started }
    pics: new Map(),
    // the catalog, the cards on screen, etc. from the first render, so the first step already knows
    // which cards are waiting (with none known it would reveal the sample cards at once)
    ...initial,
    next: 0,
    step: 0,
  };
  const status = (src) => st.pics.get(src)?.status;
  const onCard = (src) => st.visible.some((i) => st.slots[i] && st.slots[i].thumbnail_image === src);

  const load = (src) => {
    const pic = { status: 'loading', started: Date.now() };
    st.pics.set(src, pic);
    const settle = (result) => {
      if (pic.status !== 'loading') return;
      pic.status = result;
      pump();
    };
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(() => settle('ok'));
    img.onerror = () => settle('error');
    img.src = src;
    // once it counts as slow, another picture may start alongside it
    setTimeout(pump, SLOW_MS + 50);
  };

  const publish = () => setShown((prev) => (prev && prev.every((t, i) => t === st.slots[i]) ? prev : [...st.slots]));

  const pump = () => {
    if (!st.mounted) return;
    const { slots, pool, visible } = st;
    // cards hidden on this screen size let go of their designs (and nothing is downloaded for them)
    slots.forEach((t, i) => {
      if (t && !visible.includes(i)) slots[i] = null;
    });
    // every card on screen without a design takes a loaded one that isn't on another card
    const order = CHANGE_ORDER.filter((i) => visible.includes(i));
    const spare = pool.filter((t) => status(t.thumbnail_image) === 'ok' && !onCard(t.thumbnail_image));
    order.forEach((i) => {
      if (!slots[i] && spare.length) slots[i] = spare.shift();
    });
    // keep enough pictures loading for the cards still waiting (slow ones don't count),
    // plus one when the cycling has nothing ready to change to
    const waiting = order.filter((i) => !slots[i]).length;
    const want = waiting + (st.cycle && st.revealed && spare.length === 0 ? 1 : 0);
    const now = Date.now();
    let active = [...st.pics.values()].filter((p) => p.status === 'loading' && now - p.started < SLOW_MS).length;
    for (const t of pool) {
      if (active >= want) break;
      if (!st.pics.has(t.thumbnail_image)) {
        load(t.thumbnail_image);
        active += 1;
      }
    }
    if (!st.revealed) {
      // The first cards appear together: once every card has its design, or once no more designs
      // can come. The timer started at mount reveals them anyway, with the sample card where a design
      // hasn't loaded yet.
      const unsettled = pool.some((t) => status(t.thumbnail_image) !== 'ok' && status(t.thumbnail_image) !== 'error');
      if (waiting > 0 && (st.loading || unsettled)) return;
      st.revealed = true;
      clearTimeout(st.timer);
      st.timer = null;
    }
    publish();
  };

  // One card on screen that has a design changes to the next loaded design that isn't on a card.
  const tick = () => {
    if (!st.mounted || !st.revealed || !st.cycle || document.hidden || !onScreen.current) return;
    const { slots, pool, visible } = st;
    const order = CHANGE_ORDER.filter((i) => visible.includes(i) && slots[i]);
    if (!order.length) return;
    for (let n = 0; n < pool.length; n += 1) {
      const at = (st.next + n) % pool.length;
      const t = pool[at];
      if (status(t.thumbnail_image) === 'ok' && !onCard(t.thumbnail_image)) {
        slots[order[st.step % order.length]] = t;
        st.step += 1;
        st.next = at + 1;
        break;
      }
    }
    // start the picture for a later change
    const upcoming = pool.find((t) => !st.pics.has(t.thumbnail_image));
    if (upcoming) load(upcoming.thumbnail_image);
    pump();
  };

  const mount = () => {
    st.mounted = true;
    // The cards appear by FIRST_LOAD_TIMEOUT_MS after mount at the latest, even if the catalog
    // request itself is slow or never settles (they show the sample cards until designs load).
    if (!st.revealed && !st.timer) {
      st.timer = setTimeout(() => {
        st.timer = null;
        if (!st.mounted || st.revealed) return;
        st.revealed = true;
        pump();
      }, FIRST_LOAD_TIMEOUT_MS);
    }
    pump();
  };

  const unmount = () => {
    // StrictMode runs this and then mounts again, which arms a new timer
    st.mounted = false;
    clearTimeout(st.timer);
    st.timer = null;
  };

  const update = (values) => {
    Object.assign(st, values);
    pump();
  };

  return { mount, unmount, update, tick };
};

export const HeroShowcase = ({ templates, loading }) => {
  const reduceMotion = useReducedMotion();
  const wide = useMediaQuery('(min-width: 768px)');
  const pool = useMemo(() => pickShowcase(templates), [templates]);
  const visibleSlots = useMemo(() => SLOTS.map((s, i) => i).filter((i) => wide || !SLOTS[i].wide), [wide]);
  // what each card shows (a template, or null for the sample card); null until the first cards are revealed
  const [shown, setShown] = useState(null);
  const stageRef = useRef(null);
  const onScreen = useRef(true);
  const engine = useRef(null);
  if (!engine.current) engine.current = createEngine(setShown, onScreen, { pool, visible: visibleSlots, loading, cycle: !reduceMotion });

  useEffect(() => {
    const e = engine.current;
    e.mount();
    return e.unmount;
  }, []);

  useEffect(() => {
    engine.current.update({ pool, visible: visibleSlots, loading, cycle: !reduceMotion });
  }, [pool, visibleSlots, loading, reduceMotion]);

  // Pause while the hero is scrolled away.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => {
      onScreen.current = entry.isIntersecting;
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Every few seconds one card changes to the next design (not with reduced motion).
  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = setInterval(() => engine.current.tick(), CHANGE_EVERY_MS);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const fallback = !loading && pool.length === 0;

  return (
    <>
      {/* what the "is the hero on screen" check watches */}
      <div ref={stageRef} className="absolute inset-0 pointer-events-none" />
      {SLOTS.map((slot, i) => {
        // the outer cards are hidden on phones; don't mount them (and download their pictures) there
        if (slot.wide && !wide) return null;
        // Nothing until the first cards are revealed (the stage has a fixed height). After that a card
        // without a loaded design yet shows the sample card and fades to a design once one loads.
        if (!shown && !fallback) return null;
        const template = (shown && shown[i]) || null;
        return (
          // Position/rotation on the wrapper; the entrance animation's own
          // transform lives on the inner element so they don't override each other.
          <div key={i} className={`absolute ${slot.cls}`}>
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 + i * 0.08 }}
            >
              <div className="relative aspect-[5/7] overflow-hidden rounded-[6px] bg-cream shadow-[0_40px_70px_-28px_rgba(26,26,26,0.5)]">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={template ? template.id : 'sample'}
                    className="absolute inset-0"
                    initial={reduceMotion ? false : { opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={reduceMotion ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {template ? (
                      <TemplateCard template={template} />
                    ) : (
                      <SampleInvitation tone={slot.tone} kicker={slot.kicker} names={slot.names} />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        );
      })}
    </>
  );
};

export default HeroShowcase;
